import { Button } from '@heroui/button';
import { Form } from '@heroui/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { ControlledEmailInput } from '@/components/auth/controlled-email-input';
import { authClient } from '@/lib/auth-client';
import { useLoadingCallback } from '@/lib/hooks/use-loading-callback';
import { authToasts } from '@/lib/utils/toast';
import { forgotPasswordSchema } from '@/schemas/auth';

interface EmailRequestFormProps {
    onOtpSent: (email: string) => void;
}

export function EmailRequestForm({ onOtpSent }: EmailRequestFormProps) {
    const {
        control,
        handleSubmit,
        formState: { isValid },
    } = useForm<z.infer<typeof forgotPasswordSchema>>({
        resolver: zodResolver(forgotPasswordSchema),
        mode: 'onChange', // Real-time validation
    });

    const [handleRequestOtp, isLoading] = useLoadingCallback(
        async (data: z.infer<typeof forgotPasswordSchema>) => {
            const response = await authClient.emailOtp.sendVerificationOtp({
                email: data.email,
                type: 'forget-password',
            });

            if (response.error) {
                authToasts.passwordResetError(
                    response.error.message ||
                        'Failed to send verification code.'
                );
                return;
            }

            onOtpSent(data.email);
            authToasts.passwordResetCodeSent();
        }
    );

    return (
        <Form
            className="flex flex-col gap-4"
            onSubmit={handleSubmit(handleRequestOtp)}
        >
            <ControlledEmailInput control={control} name="email" />
            <Button
                color="primary"
                fullWidth
                isDisabled={!isValid}
                isLoading={isLoading}
                type="submit"
            >
                Send Verification Code
            </Button>
        </Form>
    );
}
